import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Input, Select, Textarea, FormGrid, Btn, DatePicker, Modal, useToast, RNLLoader, Spinner, SlowRequestHint, ReturnDestinationNote } from '../UI';
import * as api from '../../api';
import { belongsInMyDrafts, errorText, runChecks, missingFieldsMessage, isAwaitingCorrection, correctionTarget, OTHER_OPTION, withOtherOption, otherAwareId, otherAwareText, otherAwareValue, bypassesDepartmentReview} from '../../utils';
import HRRequisition, { pillStyle, Badge } from './HRRequisition';
import AdminCorporateServicesRequisition from './AdminCorporateServicesRequisition';
import LogisticsRequisition from './LogisticsRequisition';
import MyInbox from './MyInbox';
import MyRequisitions from './MyRequisitions';
import RequestedForField from './RequestedForField';
import RequisitionLineItemsTable, {
  blankLineItem, mapLineItemForApi, lineItemFromApi, computeItemAmount, computeRequisitionTotals,
  lineItemFromHistory,
} from './RequisitionLineItemsTable';
import PurchaseHistoryModal from './PurchaseHistoryModal';

// Department cards route to a dedicated form instead of this generic one —
// matched by name since there's no per-department "form type" flag in the schema.
// "corporate services" is the disambiguator for Admin — HR's own name also
// contains the word "administration", so a plain "administration" check would
// wrongly match both departments.
const isHRDepartmentName = (name) => (name || '').toLowerCase().includes('human resources');
const isAdminDepartmentName = (name) => (name || '').toLowerCase().includes('corporate services');
const isLogisticsDepartmentName = (name) => (name || '').toLowerCase().includes('logistics');

const GENERAL_CARD = {
  id: 'general',
  name: 'General Requisition',
  isDefault: true,
  description: 'Standard enterprise requisition — the default form with the full requisition engine.',
  tags: ['Material Purchase', 'Service Request', 'General Expenses'],
  color: '#0b318bff',
  bgColor: '#e0e7ff',
  icon: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b318bff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
  )
};

// Cycled by index so every real department gets a distinct-looking card
// regardless of how many exist — no hardcoded per-department list to keep in sync.
const DEPT_COLOR_PALETTE = [
  '#d97706', '#2563eb', '#0891b2', '#059669', '#ea580c', '#0d9488',
  '#16a34a', '#e11d48', '#7c3aed', '#dc2626', '#0284c7', '#4f46e5',
  '#c026d3', '#65a30d', '#9333ea'
];
const DEPT_BG_PALETTE = [
  '#fef3c7', '#dbeafe', '#cffafe', '#d1fae5', '#ffedd5', '#ccfbf1',
  '#dcfce7', '#ffe4e6', '#ede9fe', '#fee2e2', '#e0f2fe', '#e0e7ff',
  '#fae8ff', '#ecfccb', '#f3e8ff'
];

const GenericDeptIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
);

// A department card's address. The department's own id rather than a slug made
// from its name: a rename must not break links people have already shared, and
// two departments are free to be called the same thing.
const cardSlug = (card) => (!card ? null : (card.isDefault ? 'general' : String(card.departmentId || card.id)));

export default function NewRequisition({ onSaveSuccess, editPrId, deptSlug, subView, onNavigate }) {
  const todayDate = new Date().toISOString().split('T')[0];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDeptCard, setSelectedDeptCard] = useState(null);
  const [isHRForm, setIsHRForm] = useState(false);
  const [isAdminForm, setIsAdminForm] = useState(false);
  const [isLogisticsForm, setIsLogisticsForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Department-scoped Manager Review / My Drafts tabs, same pattern as the
  // dedicated HR/Admin forms — shown for any specific (non-General) department card.
  // 'form' | 'review' | 'drafts'
  //
  // Controlled when the caller passes subView, uncontrolled otherwise. Both are
  // aliased to the same names so every reference below is untouched — there is
  // no second code path to keep in step, and callers that pass nothing behave
  // exactly as they did before.
  const [viewState, setViewState] = useState('form');
  const view = subView || viewState;
  const setView = onNavigate ? ((v) => onNavigate(deptSlug, v)) : setViewState;
  const [reviewCount, setReviewCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  // True while My Drafts has a requisition open for correction — see the
  // drafts view below.
  const [editingDraft, setEditingDraft] = useState(false);
  const tableContainerRef = useRef(null);
  const { show: showToast } = useToast();

  // Master data
  const [confirmModal, setConfirmModal] = useState(null);
  // Persists the intended action ('SUBMITTED'/'DRAFT') across the two-phase submit flow —
  // confirmModal itself gets cleared when the approver-selection modal opens, so it can't
  // be relied on for the second executeAction() call that actually creates the PR.
  const pendingStatusRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);

  // Everything choosing a card does, EXCEPT deciding the view — lifted out of
  // the hub's click handler so arriving by URL takes the identical path.
  // Two ways to reach the same screen that configure it differently is how a
  // deep link ends up subtly wrong.
  const applyCardSelection = useCallback((card) => {
    setSelectedDeptCard(card);
    if (card?.isDefault) {
      setIsHRForm(false);
      setIsAdminForm(false);
      setIsLogisticsForm(false);
      return;
    }
    setFormData((prev) => ({ ...prev, requested_department_id: card?.departmentId }));
    setIsHRForm(!!isHRDepartmentName(card?.name));
    setIsAdminForm(!!isAdminDepartmentName(card?.name));
    setIsLogisticsForm(!!isLogisticsDepartmentName(card?.name));
  }, []);

  // The address turned back into a card. Only the three fields anything reads
  // off selectedDeptCard are needed — the colours and icon belong to the hub
  // grid, which builds its own.
  const resolveCardFromSlug = useCallback((slug) => {
    if (!slug) return null;
    if (slug === 'general') return GENERAL_CARD;
    const d = departments.find(
      (x) => String(x.id || x.department_id) === String(slug));
    if (!d) return null;
    const id = d.id || d.department_id;
    return { id: `dept-${id}`, departmentId: id, name: d.name, isDefault: false };
  }, [departments]);

  // URL -> card. Runs only when the page is driving (onNavigate present), so
  // every other caller of this component is untouched.
  //
  // Skipped while editing an existing requisition: that flow picks the card
  // from the loaded PR, and this would overwrite it with whatever the address
  // bar happened to say.
  useEffect(() => {
    if (!onNavigate || editPrId) return;
    const card = resolveCardFromSlug(deptSlug);
    if (!card) {
      // No slug, or one that matches nothing: the hub. An unknown department
      // should look like the front door rather than an empty form.
      if (selectedDeptCard) { setSelectedDeptCard(null); setIsHRForm(false); setIsAdminForm(false); setIsLogisticsForm(false); }
      return;
    }
    if (selectedDeptCard && cardSlug(selectedDeptCard) === deptSlug) return;
    applyCardSelection(card);
    // selectedDeptCard is deliberately not a dependency: this effect SETS it,
    // and listing it would re-run the moment it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptSlug, departments, onNavigate, editPrId, resolveCardFromSlug, applyCardSelection]);


  // The hub used to paint immediately with only the static General Requisition
  // card, then pop the department cards in when the fetch landed. This holds
  // the whole grid back until the departments are in, so every card — General
  // included — appears at once.
  const [hubLoading, setHubLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [purchaseRequestTypes, setPurchaseRequestTypes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  // Their requisitions skip Department Head Review, so the button must not
  // promise a review that will not happen, and afterwards they belong in My
  // Requisitions rather than a department queue they never enter.
  const skipsDepartmentReview = bypassesDepartmentReview(currentUser);
  const [suppliers, setSuppliers] = useState([]);
  const [boqDataByProject, setBoqDataByProject] = useState({});
  // Master data the adopted Admin-style grid needs.
  const [ledgers, setLedgers] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [accountLedgers, setAccountLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  // Loaded once for the whole grid and filtered per row by parent,
  // rather than a request per line as categories are chosen.
  const [expenseSubCategories, setExpenseSubCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Workflow submission modal state
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [createdPrId, setCreatedPrId] = useState(null);
  const [firstStepInfo, setFirstStepInfo] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [submittingWorkflow, setSubmittingWorkflow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // The success modal is shared by first-time submissions and resubmissions;
  // this picks the wording, since "has been created" is wrong for a
  // correction going back to the step that returned it.
  const [wasResubmit, setWasResubmit] = useState(false);
  const [submittedPrRef, setSubmittedPrRef] = useState('');

  // Populated when editing an existing PR, so a corrected RETURNED request can be
  // resubmitted straight back to the step that returned it.
  const [editWorkflowInstanceId, setEditWorkflowInstanceId] = useState(null);
  // Where Save & Resubmit will send this request. Read off the loaded
  // request rather than fetched, and shown in the confirm dialog so this
  // route names its destination like the inbox and details page do.
  const [resubmitTarget, setResubmitTarget] = useState('');
  const [resubmitComment, setResubmitComment] = useState('');
  const [editPrStatus, setEditPrStatus] = useState(null);

  const [formData, setFormData] = useState({
    pr_number: '',
    title: '',
    description: '',
    requested_for: '',
    requested_department_id: '',
    purchase_request_type_id: '',
    purchase_request_type_other: '',
    priority_status: 'normal',
    required_date: todayDate,
    expected_due_date: todayDate,
    remarks: '',
    adjustments_amount: 0,
    adjustment_reason: '',
    require_vendor: false,
    items: [{
      ...blankLineItem(todayDate),
    }]
  });

  // Manager Review pill badge — only meaningful once a specific (non-General)
  // department is in scope and that department's id is known.
  useEffect(() => {
    const deptId = formData.requested_department_id;
    if (!selectedDeptCard || selectedDeptCard.isDefault || !deptId) {
      setReviewCount(0);
      return;
    }
    let cancelled = false;
    api.getDepartmentInbox(deptId, 'Department Head Review')
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.data || []);
        setReviewCount(list.length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDeptCard, formData.requested_department_id]);

  // My Drafts pill badge — same DRAFT-status, same-department filter
  // MyRequisitions itself applies when draftsOnly is set. Re-checked whenever
  // the department changes or the user leaves the drafts tab (e.g. after
  // submitting/deleting one), so the count doesn't go stale.
  useEffect(() => {
    const deptId = formData.requested_department_id;
    if (!selectedDeptCard || selectedDeptCard.isDefault || !deptId) {
      setDraftCount(0);
      return;
    }
    let cancelled = false;
    api.listMyPRs()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.data || []);
        // Same predicate the My Drafts list uses, so the badge and the tab
        // can never disagree — a department-head bounce sits in drafts and
        // must be counted there too.
        const count = list.filter((pr) =>
          belongsInMyDrafts(pr) &&
          String(pr.requested_department_id || '') === String(deptId)
        ).length;
        setDraftCount(count);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // editingDraft: closing a correction can change what is in drafts —
    // a resubmitted requisition leaves them — so the badge is recomputed
    // when the editor closes rather than showing a stale count.
  }, [selectedDeptCard, formData.requested_department_id, view, editingDraft]);

  // After a brand-new (non-edit) draft save or submission on a specific
  // department card, we land the user on the My Drafts / Manager Review pill
  // instead of navigating away — so the form underneath needs to be blanked
  // out itself, otherwise reopening "New Requisition" would show the
  // just-saved values instead of a blank form. "General Requisition" (the
  // isDefault card) has no such pills, so it keeps the old onSaveSuccess behavior.
  const resetFormForNewEntry = async () => {
    const deptId = formData.requested_department_id;

    setFormData({
      pr_number: '',
      title: '',
      description: '',
      requested_department_id: deptId,
      purchase_request_type_id: '',
    purchase_request_type_other: '',
      priority_status: 'normal',
      required_date: todayDate,
      expected_due_date: todayDate,
      remarks: '',
      items: [{
        ...blankLineItem(todayDate)
      }]
    });
    setErrors({});
  };

  const handleSuccessModalClose = async () => {
    setShowSuccessModal(false);
    if (!editPrId && selectedDeptCard && !selectedDeptCard.isDefault) {
      const deptId = formData.requested_department_id;
      await resetFormForNewEntry();
      api.getDepartmentInbox(deptId, 'Department Head Review')
        .then((res) => setReviewCount((Array.isArray(res) ? res : (res?.data || [])).length))
        .catch(() => {});
      setView('review');
    } else {
      onSaveSuccess?.();
    }
  };

  useEffect(() => {
    // Ideally we'd fetch departments, projects, and materials here
    // For now we mock or use existing APIs
    const fetchMasterData = async () => {
      if (editPrId) setLoading(true);
      try {
        api.clearApiCache();
        const [projRes, matRes, deptRes, typeRes, userRes, , suppRes,
               ledgerRes, ccRes, accLedgerRes, expCatRes, expSubCatRes, usersRes] = await Promise.all([
          api.listProjects().catch(() => ({ data: [] })),
          api.listMaterials().catch(() => ({ data: [] })),
          api.listDepartments(0, 100, true).catch(() => ({ departments: [] })),
          api.listPurchaseRequestTypes(true).catch(() => ({ data: [] })),
          api.getMe().catch(() => (null)),
          // No number is minted here any more. The server assigns it inside the create transaction.
          Promise.resolve(null),
          api.listSuppliers().catch(() => ({ data: [] })),
          api.listLedgers(true).catch(() => ([])),
          api.listCostCentres(true).catch(() => ([])),
          api.listAccountLedgers().catch(() => ([])),
          api.listExpenseCategories().catch(() => ([])),
          api.listExpenseSubCategories().catch(() => ([])),
          api.listUsers(0, 500).catch(() => []),
        ]);
        const deptList = Array.isArray(deptRes) ? deptRes : (deptRes.departments || deptRes.data || []);
        setProjects(Array.isArray(projRes) ? projRes : (projRes.data || []));
        setMaterials(Array.isArray(matRes) ? matRes : (matRes.data || []));
        setDepartments(deptList);
        setPurchaseRequestTypes(Array.isArray(typeRes) ? typeRes : (typeRes.types || typeRes.data || []));
        setCurrentUser(userRes);
        setSuppliers(Array.isArray(suppRes) ? suppRes : (suppRes.data || suppRes.items || []));
        setLedgers(Array.isArray(ledgerRes) ? ledgerRes : (ledgerRes?.ledgers || ledgerRes?.data || []));
        setCostCentres(Array.isArray(ccRes) ? ccRes : (ccRes?.cost_centres || ccRes?.data || []));
        setAccountLedgers(Array.isArray(accLedgerRes) ? accLedgerRes : (accLedgerRes?.data || []));
        setExpenseCategories(Array.isArray(expCatRes) ? expCatRes : (expCatRes?.data || []));
        setExpenseSubCategories(Array.isArray(expSubCatRes) ? expSubCatRes : (expSubCatRes?.data || []));
        setUsers(Array.isArray(usersRes) ? usersRes : (usersRes?.data || []));

        if (editPrId) {
          const prRes = await api.getPR(editPrId);
          const pr = prRes?.data || prRes;
          const deptName = pr.department_name || pr.requested_department?.name || '';

          if (isHRDepartmentName(deptName)) {
            // HRRequisition does its own PR fetch — no need to populate the generic form.
            setIsHRForm(true);
            setSelectedDeptCard({ id: 'edit', name: deptName, isDefault: false, departmentId: pr.requested_department_id || pr.requested_department?.id });
            return;
          }
          if (isAdminDepartmentName(deptName)) {
            // AdminCorporateServicesRequisition does its own PR fetch too.
            setIsAdminForm(true);
            setSelectedDeptCard({ id: 'edit', name: deptName, isDefault: false, departmentId: pr.requested_department_id || pr.requested_department?.id });
            return;
          }

          setEditWorkflowInstanceId(pr.workflow_instance_id || null);
          setResubmitTarget(isAwaitingCorrection(pr) ? correctionTarget(pr) : '');
          setEditPrStatus((pr.request_status || '').toLowerCase());
          setFormData({
            pr_number: pr.pr_number || '',
            title: pr.title || '',
            description: pr.description || '',
            requested_for: pr.requested_for || '',
            requested_department_id: pr.requested_department_id || pr.requested_department?.id || '',
            purchase_request_type_id: otherAwareValue(
              pr.purchase_request_type_id || pr.purchase_request_type?.id, pr.purchase_request_type_other),
            purchase_request_type_other: pr.purchase_request_type_other || '',
            priority_status: pr.priority_status || 'normal',
            required_date: pr.required_date || todayDate,
            expected_due_date: pr.expected_due_date || todayDate,
            remarks: pr.remarks || '',
            adjustments_amount: pr.adjustments_amount || 0,
            adjustment_reason: pr.adjustment_reason || '',
            require_vendor: pr.require_vendor || false,
            items: (pr.items && pr.items.length > 0)
              ? pr.items.map(lineItemFromApi)
              : [{ ...blankLineItem(todayDate) }]
          });
          // Skip the card hub — department is already fixed on the draft. Locked
          // like any other specific-department card (isDefault: false).
          setSelectedDeptCard({ id: 'edit', name: deptName || 'Requisition', isDefault: false });
        } else {

        }
      } catch (e) {
        console.error(e);
      } finally {
        if (editPrId) setLoading(false);
        setHubLoading(false);
      }
    };
    fetchMasterData();
  }, [editPrId]);

  // Re-fetch materials filtered to the chosen department whenever it changes.
  // Dept-specific sub-forms (Admin, Logistics) load their own materials, so
  // this only meaningfully affects the general form here.
  useEffect(() => {
    const deptId = formData.requested_department_id;
    if (!deptId) return;
    api.listMaterials(0, undefined, '', deptId)
      .then(res => setMaterials(Array.isArray(res) ? res : (res?.data || [])))
      .catch(() => {});
  }, [formData.requested_department_id]);

  // VAT is carried by the individual lines, so the requisition's tax is the
  // sum of theirs. Same arithmetic the server runs on what it is sent.
  const { gross: grossAmount, vat: vatAmount, total: totalRequisitionValue } =
    computeRequisitionTotals(formData.items, formData.adjustments_amount);
  const historicalRefCount = new Set(
    formData.items.filter((it) => it.last_date).map((it) => it.last_vendor_id)
  ).size;

  const addHistoricalItems = (historicalItems) => {
    const newLines = historicalItems.map((h) => lineItemFromHistory(h, todayDate, { requireVendor: formData.require_vendor }));
    setFormData(prev => ({
      ...prev,
      // Drop the untouched starter row so history lines don't sit under a blank one.
      items: [...prev.items.filter((it) => it.description || it.material_id), ...newLines],
    }));
    setShowHistoryModal(false);
  };

  const handleAddItem = () => {
    const emptyItem = { ...blankLineItem(todayDate) };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, emptyItem]
    }));
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  // The shared line-items grid sends whole patches ({ boq_item_id, boq_component_id }),
  // so the patch is applied in one pass — routing each key through handleItemChange
  // separately would clobber the others off a stale formData snapshot.
  const updateItem = async (index, patch) => {
    const projectChanged = 'project_id' in patch && patch.project_id;

    setFormData(prev => {
      const items = [...prev.items];
      const next = { ...items[index], ...patch };
      if (projectChanged) {
        next.boq_item_id = '';
        next.boq_component_id = '';
      }
      if ('quantity' in patch || 'estimated_unit_rate' in patch) {
        next.estimated_amount = computeItemAmount(next);
      }
      items[index] = next;
      return { ...prev, items };
    });

    if (projectChanged && !boqDataByProject[patch.project_id]) {
      try {
        const boqRes = await api.listBOQ(patch.project_id);
        const boqItems = Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []);
        setBoqDataByProject(prev => ({ ...prev, [patch.project_id]: boqItems }));
      } catch (e) {
        console.error('Failed to load project BOQs', e);
      }
    }
  };

  const handleRemoveItem = (idx) => {
    const newItems = [...formData.items];
    newItems.splice(idx, 1);
    setFormData({ ...formData, items: newItems });
  };

  const handleSaveClick = (status) => {
    // Every row is checked. Rows only ever exist because the user clicked
    // "Add Line" — nothing appends a spare — so a blank row is an unfinished
    // line, not something to skip. Removing a row they no longer want is the
    // delete button's job, and that always leaves at least one behind.
    const rows = formData.items || [];

    const checks = [{ key: 'title', label: 'Title / Purpose', ok: !!formData.title }];
    if (rows.length === 0) checks.push({ key: 'items', label: 'At least one line item', ok: false });
    rows.forEach((item, idx) => {
      const line = `Line ${idx + 1}`;
      checks.push({ key: `project_id_${idx}`, label: `${line} project`, ok: !!item.project_id });
      checks.push({ key: `material_id_${idx}`, label: `${line} material`, ok: !!item.material_id });
    });

    const { errors: newErrors, valid, missing } = runChecks(checks);
    setErrors(newErrors);
    if (!valid) {
      // Previously this returned in silence: the borders for the per-line keys
      // were never rendered, so the button simply did nothing.
      showToast(missingFieldsMessage(missing), 'error');
      return;
    }

    if (status === 'SUBMITTED') {
      // Skip the generic "are you sure" dialog for submissions — go straight to
      // showing the first approval step (and its approver, if specific-user) so
      // the user confirms via that modal instead of a redundant generic one.
      beginSubmitFlow();
    } else {
      setConfirmModal(status);
    }
  };

  const beginSubmitFlow = async () => {
    setSaving(true);
    try {
      const stepRes = await api.getEligibleApprovers('PR_APPROVAL', {
        requested_department_id: formData.requested_department_id || null,
      });
      const data = stepRes?.data || stepRes;
      setFirstStepInfo(data);

      if (data?.approver_mode === 'specific_user') {
        const users = data.eligible_users || [];
        setApprovers(users);
        setSelectedApproverId(users.length > 0 ? (users[0].user_id || users[0].id) : '');
      } else {
        setApprovers([]);
        setSelectedApproverId('');
      }
      pendingStatusRef.current = 'SUBMITTED';
      setShowApproverModal(true);
    } catch (err) {
      console.warn("Workflow step info check failed:", err);
      showToast(`Could not start the submission: ${errorText(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const executeAction = async (overrideApproverId = null, intent = null) => {
    if (confirmModal === 'DISCARD') {
      api.clearApiCache();
      onSaveSuccess?.();
      setConfirmModal(null);
      return;
    }

    const validItems = formData.items.filter(item => item.description || item.project_id || item.material_id);
    // The caller says what it is doing. This used to be inferred as
    // `confirmModal || pendingStatusRef.current`, which let a stale
    // confirmModal outrank the submit intent: a failed save left confirmModal
    // set to 'DRAFT' (the catch block never cleared it), so the NEXT submit
    // resolved to 'DRAFT' and was silently saved as a draft with no workflow
    // started. The explicit argument wins; the old sources remain only as a
    // fallback for callers that have not been given one.
    const status = intent || confirmModal || pendingStatusRef.current;

    setSaving(true);
    try {
      const isSubmitting = status === 'SUBMITTED';
      let approverId = overrideApproverId || selectedApproverId;
      if (firstStepInfo?.approver_mode !== 'specific_user' || !approverId) {
        approverId = undefined;
      }

      const mappedItems = validItems.map(it => mapLineItemForApi(it, { requireVendor: formData.require_vendor }));

      // Totalled from the lines actually being sent, not from every row on
      // screen — a blank row is dropped above, and the backend rejects the
      // save if its own sum of the payload disagrees with this figure.
      const { vat: payloadVat, total: payloadTotal } =
        computeRequisitionTotals(validItems, formData.adjustments_amount);

      let res;
      if (editPrId) {
        res = await api.updatePR(editPrId, {
          title: formData.title,
          description: formData.description || null,
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          remarks: formData.remarks || null,
          vat_amount: Number(payloadVat.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          total_estimated_cost: payloadTotal,
          items: mappedItems,
        });
        if (status === 'RESUBMIT' && editWorkflowInstanceId) {
          await api.resubmitWorkflow(editWorkflowInstanceId, resubmitComment.trim() || null);
        } else if (isSubmitting && !editWorkflowInstanceId) {
          // Submitting a saved draft. updatePR only persists the edits — unlike
          // createPR it has no submit_for_approval flag — so without this the
          // request silently stayed a draft. Mirrors how My Requisitions
          // submits a draft straight from the list.
          await api.submitWorkflow({
            entity_type: 'purchase_request',
            entity_id: editPrId,
            definition_code: 'PR_APPROVAL',
            first_approver_id: approverId,
          });
        }
      } else {
        const payload = {
          pr_number: formData.pr_number || undefined,
          title: formData.title,
          description: formData.description,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          remarks: formData.remarks || null,
          vat_amount: Number(payloadVat.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          items: mappedItems,
          total_estimated_cost: payloadTotal,
          submit_for_approval: isSubmitting,
          first_approver_id: isSubmitting ? approverId : undefined
        };
        res = await api.createPR(payload);
      }

      api.clearApiCache();
      // A resubmit is a submission as far as the user is concerned — it puts
      // the requisition back in front of an approver — so it gets the same
      // confirmation modal rather than a toast that scrolls away unnoticed.
      const isResubmitting = status === 'RESUBMIT' && !!editWorkflowInstanceId;
      if (isSubmitting || isResubmitting) {
        setSubmittedPrRef(res?.pr_number || formData.pr_number || '');
        setWasResubmit(isResubmitting);
        setConfirmModal(null);
        setShowApproverModal(false);
        setShowSuccessModal(true);
      } else {
        showToast(
          status === 'RESUBMIT'
            ? "Requisition updated and resubmitted for approval!"
            : (editPrId
                ? "Requisition updated!"
                : `Requisition ${res?.pr_number || ''} saved to Draft!`.replace('  ', ' ')),
          "success"
        );
        setConfirmModal(null);
        setShowApproverModal(false);
        // A bypassing requester never enters the department queue, so sending
        // them to it would show an empty list and hide the requisition they
        // just raised. They go straight to My Requisitions, where it now is.
        if (!editPrId && !skipsDepartmentReview && selectedDeptCard && !selectedDeptCard.isDefault) {
          await resetFormForNewEntry();
          setView('drafts');
        } else if (onSaveSuccess) {
          onSaveSuccess();
        }
      }
    } catch (e) {
      console.error(e);
      showToast(`Could not save this requisition: ${errorText(e)}`, "error");
      // Cleared on failure too. Left set, it became the `status` of whatever
      // the user tried next.
      setConfirmModal(null);
    } finally {
      setSaving(false);
      setSubmittingWorkflow(false);
      pendingStatusRef.current = null;
    }
  };

  const handleFinalWorkflowSubmit = async () => {
    setSubmittingWorkflow(true);
    await executeAction(selectedApproverId, 'SUBMITTED');
  };

  // Shared by both the card hub and the form's Department field: which
  // department(s) the current user actually belongs to, matched against the
  // full DB department list — used to gate card visibility AND to restrict
  // the "General Requisition" Department dropdown to the user's own dept(s).
  const isSuperAdmin = currentUser?.roles?.some(r => {
    const name = (r.name || r.role_name || r || '').toString().toLowerCase();
    return name === 'super_admin' || name === 'superadmin';
  }) || false;

  // Who may raise a General Requisition — the free-form card that is not tied
  // to any department form. Restricted because it lets the requester pick the
  // department themselves, which is only appropriate for people who either
  // sit above departments (super admin, MD, DMD) or bypass department review
  // altogether.
  const GENERAL_CARD_ROLES = new Set([
    'super_admin', 'superadmin', 'md', 'dmd', 'bypass_department_review',
  ]);
  const canRaiseGeneralRequisition = currentUser?.roles?.some((r) => {
    const name = (r.name || r.role_name || r || '').toString().toLowerCase();
    return GENERAL_CARD_ROLES.has(name);
  }) || false;

  const userMappedDepts = currentUser?.departments || currentUser?.department_mapping || [];
  const userDeptNames = new Set(
    userMappedDepts.map(d => (d.name || d.department_name || d.department?.name || '').toString().toLowerCase())
  );
  const userDeptIds = new Set(
    userMappedDepts.map(d => (d.id || d.department_id || d.department?.id || '').toString())
  );
  const userDepartmentOptions = departments.filter(d => {
    const idStr = (d.id || d.department_id || '').toString();
    if (userDeptIds.has(idStr)) return true;
    const nameLower = (d.name || '').toLowerCase();
    return userDeptNames.has(nameLower);
  });
  // Fall back to the full list if we couldn't match anything, rather than
  // handing the requester an empty dropdown.
  const generalDeptOptions = userDepartmentOptions.length > 0 ? userDepartmentOptions : departments;

  // Editing an existing PR skips the hub entirely (its department is already
  // fixed) — but that decision only happens once the PR fetch resolves and
  // calls setSelectedDeptCard. Without this guard, the hub would flash (or
  // stick, if the fetch is slow) before landing on the actual edit form.
  if (editPrId && loading) {
    return <RNLLoader title="Loading Requisition..." subtitle="Fetching request details" />;
  }

  if (isHRForm) {
    return (
      <HRRequisition
        subView={subView}
        onViewChange={onNavigate ? ((v) => onNavigate(deptSlug, v)) : undefined}
        editPrId={editPrId}
        department={{ id: selectedDeptCard?.departmentId, name: selectedDeptCard?.name }}
        onBack={() => {
          if (editPrId) { onSaveSuccess?.(); }
          else if (onNavigate) onNavigate(null, null);
          else { setIsHRForm(false); setSelectedDeptCard(null); }
        }}
        onSaveSuccess={onSaveSuccess}
      />
    );
  }

  if (isAdminForm) {
    return (
      <AdminCorporateServicesRequisition
        subView={subView}
        onViewChange={onNavigate ? ((v) => onNavigate(deptSlug, v)) : undefined}
        editPrId={editPrId}
        department={{ id: selectedDeptCard?.departmentId, name: selectedDeptCard?.name }}
        onBack={() => {
          if (editPrId) { onSaveSuccess?.(); }
          else if (onNavigate) onNavigate(null, null);
          else { setIsAdminForm(false); setSelectedDeptCard(null); }
        }}
        onSaveSuccess={onSaveSuccess}
      />
    );
  }

  if (isLogisticsForm) {
    return (
      <LogisticsRequisition
        subView={subView}
        onViewChange={onNavigate ? ((v) => onNavigate(deptSlug, v)) : undefined}
        editPrId={editPrId}
        department={{ id: selectedDeptCard?.departmentId, name: selectedDeptCard?.name }}
        onBack={() => {
          if (editPrId) { onSaveSuccess?.(); }
          else if (onNavigate) onNavigate(null, null);
          else { setIsLogisticsForm(false); setSelectedDeptCard(null); }
        }}
        onSaveSuccess={onSaveSuccess}
      />
    );
  }

  // ----------------------------------------------------
  // VIEW 1: DEPARTMENT & REQUISITION TYPE SELECTION HUB
  // ----------------------------------------------------
  if (!selectedDeptCard) {
    // One card per real department, generated live — so the count always
    // matches what's actually in the system, and selecting a card carries
    // the department's real ID (no fragile name-matching required).
    const deptCards = [
      GENERAL_CARD,
      ...departments.map((d, idx) => ({
        id: `dept-${d.id || d.department_id}`,
        departmentId: d.id || d.department_id,
        name: d.name,
        isDefault: false,
        description: d.description || `Departmental requisition form for ${d.name}.`,
        tags: [],
        color: DEPT_COLOR_PALETTE[idx % DEPT_COLOR_PALETTE.length],
        bgColor: DEPT_BG_PALETTE[idx % DEPT_BG_PALETTE.length],
        icon: <GenericDeptIcon color={DEPT_COLOR_PALETTE[idx % DEPT_COLOR_PALETTE.length]} />
      }))
    ];

    // Filter cards based on user access
    const availableCards = deptCards.filter(card => {
      // General Requisition is restricted — see canRaiseGeneralRequisition.
      if (card.isDefault) return canRaiseGeneralRequisition;
      // Super Admin sees all cards
      if (isSuperAdmin) return true;
      // Standard user sees cards for the department(s) they're actually mapped to
      return userDeptIds.has((card.departmentId || '').toString());
    });

    const filteredCards = availableCards.filter(card => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return card.name.toLowerCase().includes(q) ||
        card.description.toLowerCase().includes(q) ||
        card.tags.some(t => t.toLowerCase().includes(q));
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Header & Subtitle */}
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Raise New Requisition</h2>
          <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>
            Select the department or requisition type to create the appropriate requisition form.
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative', width: '100%' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search department, requisition type, recent forms, frequently used..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '14px 16px 14px 48px',
              borderRadius: 10,
              border: '1px solid black',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
              background: '#ffffff'
            }}
          />
        </div>

        {/* Section Header */}
        <div style={{ fontSize: 13, fontWeight: 700, color: '#000408ff', letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 4 }}>
          DEPARTMENTS & REQUISITION TYPES
        </div>

        {/* Department Cards Grid */}
        {hubLoading ? (
          <div style={{ padding: '40px 0' }}>
            <RNLLoader title="Loading requisition forms…" subtitle="Fetching your departments" />
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {filteredCards.map(card => {
            const handleSelectCard = () => {
              if (onNavigate) { onNavigate(cardSlug(card), null); return; }
              applyCardSelection(card);
              setView('form');
              if (card.isDefault) {
                // General Requisition: department stays editable, but default
                // it to the requester's own department so it isn't left blank.
                const firstDept = generalDeptOptions[0];
                if (firstDept) {
                  setFormData(prev => ({ ...prev, requested_department_id: firstDept.id || firstDept.department_id }));
                }
                return;
              }
              // Specific department card: department is locked to that exact department.
              setFormData(prev => ({ ...prev, requested_department_id: card.departmentId }));
              if (isHRDepartmentName(card.name)) setIsHRForm(true);
              if (isAdminDepartmentName(card.name)) setIsAdminForm(true);
            };

            return (
              <div
                key={card.id}
                onClick={handleSelectCard}
                style={{
                  background: '#c4d4f6ff',
                  border: '2px solid black',
                  borderRadius: 12,
                  padding: 20,
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  gap: 16,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  position: 'relative'
                }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 20px -5px rgba(0,0,0,0.1)'; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div>
                  {/* Icon & Title Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
                    <div style={{ background: card.bgColor, width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {card.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{card.name}</h3>
                        {card.isDefault && (
                          <span style={{ background: '#7c3aed', color: '#ffffff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>
                        {card.description}
                      </p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                    {card.tags.map((tag, idx) => (
                      <span key={idx} style={{ background: '#e0e1e3ff', color: '#080a0fff', fontSize: 11, fontWeight: 900, padding: '2px 8px', borderRadius: 4, border: '1px solid #b1bbd5ff' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Open Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSelectCard(); }}
                    style={{
                      background: '#0b318bff',
                      color: '#ffffff',
                      border: '2px solid #0b0b0bff',
                      padding: '8px 20px',
                      borderRadius: 20,
                      fontWeight: 900,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      boxShadow: 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#1d4ed8'}
                    onMouseOut={e => e.currentTarget.style.background = '#0b318bff'}
                  >
                    Open &gt;
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  }

  // ----------------------------------------------------
  // VIEW 2: REQUISITION FORM (EXISTING DESIGN)
  // ----------------------------------------------------
  const backButton = (
    <button
      type="button"
      onClick={() => {
        api.clearApiCache();
        if (editPrId) { onSaveSuccess?.(); }
        else if (onNavigate) { onNavigate(null, null); }
        else { setSelectedDeptCard(null); }
      }}
      style={{
        padding: '8px 16px',
        borderRadius: 8,
        border: '1px solid black',
        background: '#0b318bff',
        color: '#ffffff',
        fontWeight: 900,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13
      }}
    >
      ← <span style={{ fontWeight: 900 }}>{editPrId ? 'Back to My Requisitions' : 'Back to Department Hub'}</span>
    </button>
  );

  // A specific (non-General) department card gets the same Manager Review /
  // My Drafts tabs as the dedicated HR/Admin forms — "General Requisition"
  // doesn't represent one single department, so it's excluded.
  // Suppressed while editing one requisition. In that mode this component is
  // rendered *inside* My Drafts, which already sits under the department hub's
  // own tab row — so drawing them again duplicated the whole nav. They would
  // also be a trap: switching tab mid-edit silently abandons the correction.
  const showDeptTabs = !!(selectedDeptCard && !selectedDeptCard.isDefault && !editPrId);
  const pillRow = showDeptTabs && (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button onClick={() => setView('form')} style={pillStyle(view === 'form')}>New Requisition</button>
      <button onClick={() => setView('review')} style={pillStyle(view === 'review')}>{selectedDeptCard.name} Manager Review {reviewCount > 0 && <Badge count={reviewCount} />}</button>
      <button onClick={() => setView('drafts')} style={pillStyle(view === 'drafts')}>My Drafts {draftCount > 0 && <Badge count={draftCount} />}</button>
    </div>
  );

  if (showDeptTabs && view === 'review') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a request is open for correction — that editor brings
            its own "Back to My Requisitions". */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyInbox onEditingChange={setEditingDraft} stepNameFilter="Department Head Review" title={`${selectedDeptCard.name} Manager Review`} departmentId={formData.requested_department_id} />
      </div>
    );
  }

  if (showDeptTabs && view === 'drafts') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a returned requisition is open for correction: that
            editor brings its own "Back to My Requisitions", and the hub's back
            bar and tabs would sit above it as a second, competing way out. */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyRequisitions
          title="My Drafts"
          departmentId={formData.requested_department_id}
          draftsOnly
          onEditingChange={setEditingDraft}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Top Banner Navigation back to cards hub */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: -8 }}>
        {backButton}
        {selectedDeptCard && (
          <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
            Selected Form Type: <span style={{ color: '#0b318bff', fontWeight: 700 }}>{selectedDeptCard.name}</span>
          </div>
        )}
      </div>

      {pillRow}

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#0b318bff' }}>Requisition Header</h3>
        <FormGrid cols={4}>
          {/* ROW 1 */}
          <div style={{ gridColumn: 'span 1' }}>
            {/* Empty until the server issues one. It is no longer minted when
                the form opens, because that reserved nothing and two people
                opening a form together were shown the same number. */}
            <Input
              label="Requisition Ref ID"
              value={formData.pr_number}
              placeholder="Will be generated on submission"
              disabled
              style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }}
            />
            {!formData.pr_number && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                Generated automatically when you submit or save.
              </div>
            )}
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <Input
              label="Title / Purpose"
              placeholder="e.g. Purchase of electrical materials for site installation"
              value={formData.title}
              style={{ border: errors.title && !formData.title ? '2px solid #ef4444' : undefined }}
              onChange={v => setFormData({ ...formData, title: v })}
            />
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <Input
              label="Requester"
              disabled
              value={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : ''}
              style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }}
            />
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <RequestedForField
              value={formData.requested_for}
              onChange={(v) => setFormData({ ...formData, requested_for: v })}
            />
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <Select
              label="Department"
              options={(editPrId || selectedDeptCard?.isDefault ? (editPrId ? departments : generalDeptOptions) : departments).map(d => ({ value: d.id || d.department_id, label: d.name }))}
              value={formData.requested_department_id}
              onChange={v => setFormData({ ...formData, requested_department_id: v })}
              disabled={!editPrId && !selectedDeptCard?.isDefault}
            />
          </div>

          {/* ROW 2 */}
          <div style={{ gridColumn: 'span 1' }}>
            <Select
              label="Request Type"
              options={withOtherOption(purchaseRequestTypes.map(t => ({ value: t.id || t.type_id, label: t.name })))}
              value={formData.purchase_request_type_id}
              onChange={v => setFormData({ ...formData, purchase_request_type_id: v })}
            />
            {formData.purchase_request_type_id === OTHER_OPTION && (
              <Input
                label="Specify Request Type"
                value={formData.purchase_request_type_other}
                onChange={(v) => setFormData({ ...formData, purchase_request_type_other: v })}
                placeholder="Type the request type"
                required
              />
            )}
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <Select
              label="Priority Status"
              options={[
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
                { value: 'critical', label: 'Critical' },
              ]}
              value={formData.priority_status}
              onChange={v => setFormData({ ...formData, priority_status: v })}
            />
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <DatePicker
              label="Required Date"
              value={formData.required_date}
              onChange={v => setFormData({ ...formData, required_date: v })}
            />
          </div>
          <div style={{ gridColumn: 'span 1' }}>
            <DatePicker
              label="Expected Due Date"
              value={formData.expected_due_date}
              onChange={v => setFormData({ ...formData, expected_due_date: v })}
            />
          </div>

          {/* ROW 3 */}
          {/* Forced to column 1 so the two text areas always start a fresh
              row and stay side by side. The single-width fields above them
              are an odd count, so left to flow, Purpose slid up beside
              Expected Due Date and pushed Remarks onto a row of its own. */}
          <div style={{ gridColumn: '1 / span 2' }}>
            <Textarea
              label="Purpose / Justification"
              rows={2}
              placeholder="Brief purpose or justification"
              value={formData.description}
              onChange={v => setFormData({ ...formData, description: v })}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Textarea
              label="Remarks"
              rows={2}
              placeholder="Any additional remarks..."
              value={formData.remarks}
              onChange={v => setFormData({ ...formData, remarks: v })}
            />
          </div>
        </FormGrid>
      </Card>

      <Card style={{ padding: 0, border: '1px solid black', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0b318bff', margin: 0 }}>Line Items</h3>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {formData.items.length} item(s) · Total: ₦{grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {historicalRefCount} historical ref(s)
              </div>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', fontSize: 12.5, fontWeight: 600, color: formData.require_vendor ? '#0b318b' : '#64748b' }}>
              <div
                onClick={() => setFormData(prev => {
                  const turningOff = prev.require_vendor;
                  return {
                    ...prev,
                    require_vendor: !prev.require_vendor,
                    ...(turningOff && {
                      items: prev.items.map(it => ({ ...it, vendor_id: '', vendor_other: '' })),
                    }),
                  };
                })}
                style={{
                  width: 36, height: 20, borderRadius: 10, background: formData.require_vendor ? '#0b318b' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 2, left: formData.require_vendor ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              Require a Vendor?
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={() => setShowHistoryModal(true)} style={{ background: '#f97316', color: '#fff', border: 'none' }}>Select From Previous Purchases</Btn>
            <Btn onClick={handleAddItem} style={{ background: '#0b318bff', color: '#fff', border: 'none' }}>Add Line</Btn>
          </div>
        </div>

        <div ref={tableContainerRef} style={{ maxHeight: 450, overflowX: 'auto', overflowY: 'auto', scrollBehavior: 'smooth' }}>
          <RequisitionLineItemsTable
            items={formData.items}
            updateItem={updateItem}
            removeItem={handleRemoveItem}
            errors={errors}
            materials={materials}
            suppliers={suppliers}
            projects={projects}
            boqDataByProject={boqDataByProject}
            expenseCategories={expenseCategories}
            expenseSubCategories={expenseSubCategories}
            costCentres={costCentres}
            accountLedgers={accountLedgers}
            ledgers={ledgers}
            users={users}
            requireVendor={formData.require_vendor}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 24, padding: 20, borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ minWidth: 220 }}>
            <Input label="Adjustments (₦)" type="number" value={formData.adjustments_amount} onChange={(v) => setFormData({ ...formData, adjustments_amount: v })} />
          </div>
          <div style={{ minWidth: 260 }}>
            <Input label="Adjustment Reason" value={formData.adjustment_reason} onChange={(v) => setFormData({ ...formData, adjustment_reason: v })} />
          </div>
          <div style={{ textAlign: 'right' }}>
            {/* Spelled out so an approver can see what the total is made of
                rather than having to add the lines back up. */}
            <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>
              Sub-total ₦{grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' · '}
              VAT ₦{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>TOTAL ESTIMATED COST</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0b318bff' }}>
              ₦{totalRequisitionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </Card>

      {/* Footer Buttons */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          onClick={() => setConfirmModal('DISCARD')}
          disabled={saving}
          style={{
            background: '#ef4444', color: '#ffffff', border: 'none',
            padding: '12px 24px', borderRadius: 8, fontWeight: 900,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#dc2626'}
          onMouseOut={e => e.currentTarget.style.background = '#ef4444'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          Cancel
        </button>
        <button
          onClick={() => handleSaveClick('DRAFT')}
          disabled={saving}
          style={{
            background: '#eab308', color: '#fff', border: 'none',
            padding: '12px 24px', borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s'
          }}
          onMouseOver={e => !saving && (e.currentTarget.style.background = '#facc15')}
          onMouseOut={e => !saving && (e.currentTarget.style.background = '#eab308')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
          {editPrId ? 'Save Changes' : 'Save to Draft'}
        </button>
        {editPrId && editPrStatus === 'returned' && (
          <button
            onClick={() => handleSaveClick('RESUBMIT')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none',
              padding: '12px 24px', borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s',
              opacity: saving ? 0.85 : 1,
            }}
            onMouseOver={e => !saving && (e.currentTarget.style.background = '#22c55e')}
            onMouseOut={e => !saving && (e.currentTarget.style.background = '#16a34a')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            Save &amp; Resubmit
          </button>
        )}
        {!editPrId && (
          <button
            onClick={() => handleSaveClick('SUBMITTED')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none',
              padding: '12px 24px', borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s'
            }}
            onMouseOver={e => !saving && (e.currentTarget.style.background = '#22c55e')}
            onMouseOut={e => !saving && (e.currentTarget.style.background = '#16a34a')}
          >
            {saving
              ? <Spinner size={15} />
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>}
            {saving ? 'Submitting…' : (skipsDepartmentReview ? 'Submit to Cost Control' : 'Submit to Department Head')}
          </button>
        )}
      </div>

      {showHistoryModal && (
        <PurchaseHistoryModal onCancel={() => setShowHistoryModal(false)} onAdd={addHistoricalItems} />
      )}

      {/* Mirrors the inbox's Authorize Request dialog — same width, same
          layout, same wording — because it is the same action underneath:
          resubmit delegates to approve when a request was returned to its
          holder for correction. Two dialogs for one action is what made the
          three routes look like three different things. */}
      {confirmModal === 'RESUBMIT' && (
        <Modal title="Authorize Request" onClose={saving ? () => { } : () => setConfirmModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>save and resubmit</strong> the purchase request
              <strong> {formData.pr_number}</strong>.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment
              </label>
              <Input
                value={resubmitComment}
                onChange={setResubmitComment}
                placeholder="Optional comment..."
                as="textarea"
                rows={3}
                disabled={saving}
              />
            </div>

            {resubmitTarget && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Goes back to - {resubmitTarget}
                </label>
                <ReturnDestinationNote stepName={resubmitTarget} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" disabled={saving} onClick={() => setConfirmModal(null)}>Cancel</Btn>
            <Btn
              onClick={() => executeAction(null, confirmModal)}
              loading={saving}
              style={{ background: '#16a34a', color: '#fff', border: 'none' }}
            >
              {saving ? 'Resubmitting…' : 'Confirm Resubmit'}
            </Btn>
          </div>
        </Modal>
      )}

      {confirmModal && confirmModal !== 'RESUBMIT' && (
        <Modal title="Confirm Action" onClose={saving ? () => { } : () => setConfirmModal(null)} width={400}>
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 15, color: '#475569', marginBottom: 24 }}>
              {confirmModal === 'DISCARD' && "Are you sure you want to discard all changes? This action cannot be undone."}
              {confirmModal === 'DRAFT' && "Are you sure you want to save this requisition as a draft? You can submit it later."}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Btn variant="outline" disabled={saving} onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn
                onClick={() => executeAction(null, confirmModal)}
                disabled={saving}
                style={{
                  background: confirmModal === 'DISCARD' ? '#ef4444' : (confirmModal === 'RESUBMIT' ? '#16a34a' : '#eab308'),
                  color: '#fff', border: 'none'
                }}
              >
                {saving ? 'Processing...' : 'Confirm'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {showApproverModal && (
        <Modal title="Submit for Approval"
          onClose={submittingWorkflow ? () => { } : () => { pendingStatusRef.current = null; setShowApproverModal(false); }}>
          <div style={{ padding: 16 }}>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569' }}>
              This requisition will be sent for approval, starting with the step below.
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              First Approver{firstStepInfo?.step_name ? ` - ${firstStepInfo.step_name}` : ''}
            </label>
            {firstStepInfo?.approver_mode === 'specific_user' ? (
              <Select 
                value={selectedApproverId}
                onChange={setSelectedApproverId}
                options={approvers.map(a => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
              />
            ) : (
              <Select
                value="step_queue"
                onChange={() => {}}
                options={[{ value: 'step_queue', label: `🏢 ${firstStepInfo?.step_name || 'Approval Step'}` }]}
              />
            )}
          </div>
          <SlowRequestHint active={submittingWorkflow} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <Btn variant="outline" disabled={submittingWorkflow} onClick={() => { pendingStatusRef.current = null; setShowApproverModal(false); }}>Cancel</Btn>
            <Btn onClick={handleFinalWorkflowSubmit} loading={submittingWorkflow} style={{ background: '#16a34a', color: '#fff', border: 'none' }}>
              {submittingWorkflow ? 'Submitting…' : 'Submit PR'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Submission Success Confirmation Modal with Green Checkmark */}
      {showSuccessModal && (
        <Modal hideHeader width={420} onClose={handleSuccessModalClose}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#dcfce7',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
              boxShadow: '0 0 0 8px #f0fdf4'
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>

            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              {wasResubmit ? 'Requisition Resubmitted!' : 'Requisition Submitted!'}
            </h3>

            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              {wasResubmit ? (
                submittedPrRef ? (
                  <>Requisition <strong style={{ color: '#0b318bff' }}>{submittedPrRef}</strong> has been corrected and sent back to the step that returned it.</>
                ) : (
                  <>Your corrections have been saved and the requisition has been sent back for review.</>
                )
              ) : submittedPrRef ? (
                <>Requisition <strong style={{ color: '#0b318bff' }}>{submittedPrRef}</strong> has been created and successfully submitted to {firstStepInfo?.step_name || 'the first approval step'} for processing.</>
              ) : (
                <>Your purchase requisition has been created and successfully submitted for approval.</>
              )}
            </p>

            <button
              onClick={handleSuccessModalClose}
              style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: '#fff',
                border: 'none',
                padding: '10px 32px',
                borderRadius: 20,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'none'}
            >
              Great, Thanks!
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
